import { startAuthentication } from '@simplewebauthn/browser';
import type { User, AuthTokens } from '@weather-story/shared-types';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api } from '../services/api';
import { syncConsentFromServer } from '../utils/cookieConsent';

const STORE_KEY = 'weather-story-auth';
const REMEMBER_KEY = 'weather-story-remember-me';

// Cache passkey authenticate-options to avoid hitting the API on every page load.
// Server-side challenge TTL is 5 min, so we cache for 3 min to stay safely within bounds.
const PASSKEY_OPTIONS_CACHE_TTL = 3 * 60 * 1000;
let cachedPasskeyOptions: { data: Record<string, unknown>; expiresAt: number } | null = null;

const authStorage = createJSONStorage(() => ({
  getItem: (name) => {
    const rememberMe = localStorage.getItem(REMEMBER_KEY) !== 'false';
    if (rememberMe) {
      return localStorage.getItem(name);
    }
    return sessionStorage.getItem(name) ?? localStorage.getItem(name);
  },
  setItem: (name, value) => {
    const rememberMe = localStorage.getItem(REMEMBER_KEY) !== 'false';
    if (rememberMe) {
      localStorage.setItem(name, value);
      sessionStorage.removeItem(name);
    } else {
      sessionStorage.setItem(name, value);
      localStorage.removeItem(name);
    }
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
}));

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingEmail: string | null;
  onboardingCompleted: boolean;

  // Actions
  sendMagicLink: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<{ isNewUser: boolean }>;
  handleMagicLinkCallback: (
    accessToken: string,
    refreshToken: string
  ) => Promise<{ isNewUser: boolean }>;
  googleSignIn: (params: {
    accessToken?: string;
    idToken?: string;
  }) => Promise<{ isNewUser: boolean }>;
  passkeySignIn: () => Promise<{ isNewUser: boolean }>;
  passkeyConditionalSignIn: () => Promise<{ isNewUser: boolean }>;
  logout: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  setOnboardingCompleted: () => void;
  clearError: () => void;
  clearPendingEmail: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      pendingEmail: null,
      onboardingCompleted: false,

      sendMagicLink: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          await api.post('/auth/magic-link', { email });
          set({ pendingEmail: email, isLoading: false });
        } catch (error: unknown) {
          set({
            error: error instanceof Error ? error.message : 'Failed to send sign-in code',
            isLoading: false,
          });
          throw error;
        }
      },

      verifyCode: async (email: string, code: string) => {
        set({ isLoading: true, error: null });
        const rememberMe = localStorage.getItem(REMEMBER_KEY) !== 'false';
        try {
          const response = await api.post<{
            user: User;
            tokens: AuthTokens;
            isNewUser: boolean;
            rememberMe: boolean;
          }>('/auth/verify-code', { email, code, rememberMe });

          const data = response.data!;
          set({
            user: data.user,
            accessToken: data.tokens.accessToken,
            refreshToken: data.tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            pendingEmail: null,
            onboardingCompleted: !data.isNewUser,
          });
          syncConsentFromServer();
          return { isNewUser: data.isNewUser };
        } catch (error: unknown) {
          set({
            error: error instanceof Error ? error.message : 'Invalid code',
            isLoading: false,
          });
          throw error;
        }
      },

      handleMagicLinkCallback: async (accessToken: string, refreshToken: string) => {
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: true,
          error: null,
        });

        try {
          // Fetch user data and onboarding status
          const response = await api.get<{
            user: User;
            onboardingCompleted: boolean;
          }>('/auth/me');

          const data = response.data!;
          set({
            user: data.user,
            onboardingCompleted: data.onboardingCompleted,
            isLoading: false,
            pendingEmail: null,
          });
          syncConsentFromServer();

          return { isNewUser: !data.onboardingCompleted };
        } catch (error: unknown) {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Authentication failed',
          });
          throw error;
        }
      },

      googleSignIn: async (params: { accessToken?: string; idToken?: string }) => {
        set({ isLoading: true, error: null });
        const rememberMe = localStorage.getItem(REMEMBER_KEY) !== 'false';
        try {
          const response = await api.post<{
            user: User;
            tokens: AuthTokens;
            isNewUser: boolean;
            rememberMe: boolean;
          }>('/auth/google', { ...params, rememberMe });

          const data = response.data!;
          set({
            user: data.user,
            accessToken: data.tokens.accessToken,
            refreshToken: data.tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            pendingEmail: null,
            onboardingCompleted: !data.isNewUser,
          });
          syncConsentFromServer();
          return { isNewUser: data.isNewUser };
        } catch (error: unknown) {
          set({
            error: error instanceof Error ? error.message : 'Google sign-in failed',
            isLoading: false,
          });
          throw error;
        }
      },

      passkeySignIn: async () => {
        set({ isLoading: true, error: null });
        const rememberMe = localStorage.getItem(REMEMBER_KEY) !== 'false';
        try {
          // Get authentication options from server
          const optionsRes = await api.post<Record<string, unknown>>(
            '/auth/passkey/authenticate-options',
            {}
          );
          const options =
            optionsRes.data! as unknown as import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON;

          // Trigger browser passkey prompt
          const assertion = await startAuthentication({ optionsJSON: options });

          // Send assertion to server for verification
          const response = await api.post<{
            user: User;
            tokens: AuthTokens;
            isNewUser: boolean;
            rememberMe: boolean;
          }>('/auth/passkey/authenticate', { response: assertion, rememberMe });

          const data = response.data!;
          set({
            user: data.user,
            accessToken: data.tokens.accessToken,
            refreshToken: data.tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            pendingEmail: null,
            onboardingCompleted: true,
          });
          syncConsentFromServer();
          return { isNewUser: false };
        } catch (error: unknown) {
          set({
            error:
              error instanceof DOMException && error.name === 'NotAllowedError'
                ? 'Passkey authentication was cancelled.'
                : error instanceof Error
                  ? error.message
                  : 'Passkey sign-in failed',
            isLoading: false,
          });
          throw error;
        }
      },

      passkeyConditionalSignIn: async () => {
        const rememberMe = localStorage.getItem(REMEMBER_KEY) !== 'false';
        try {
          // Use cached options if still valid to avoid an API call on every page load
          let optionsData: Record<string, unknown>;
          if (cachedPasskeyOptions && Date.now() < cachedPasskeyOptions.expiresAt) {
            optionsData = cachedPasskeyOptions.data;
          } else {
            const optionsRes = await api.post<Record<string, unknown>>(
              '/auth/passkey/authenticate-options',
              {}
            );
            optionsData = optionsRes.data!;
            cachedPasskeyOptions = {
              data: optionsData,
              expiresAt: Date.now() + PASSKEY_OPTIONS_CACHE_TTL,
            };
          }
          const options =
            optionsData as unknown as import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON;

          // Conditional UI: browser shows passkey in autocomplete dropdown
          const assertion = await startAuthentication({
            optionsJSON: options,
            useBrowserAutofill: true,
          });

          const response = await api.post<{
            user: User;
            tokens: AuthTokens;
            isNewUser: boolean;
            rememberMe: boolean;
          }>('/auth/passkey/authenticate', { response: assertion, rememberMe });

          const data = response.data!;
          set({
            user: data.user,
            accessToken: data.tokens.accessToken,
            refreshToken: data.tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            pendingEmail: null,
            onboardingCompleted: true,
          });
          syncConsentFromServer();
          return { isNewUser: false };
        } catch (error: unknown) {
          // AbortError is expected when user navigates away — don't set error state
          if (error instanceof DOMException && error.name === 'AbortError') {
            throw error;
          }
          throw error;
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          if (refreshToken) {
            await api.post('/auth/logout', { refreshToken });
          }
        } catch {
          // Ignore logout errors
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            pendingEmail: null,
            onboardingCompleted: false,
          });
        }
      },

      refreshTokens: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        try {
          const response = await api.post<{ tokens: AuthTokens }>('/auth/refresh', {
            refreshToken,
          });

          const data = response.data!;
          set({
            accessToken: data.tokens.accessToken,
            refreshToken: data.tokens.refreshToken,
          });
        } catch (error) {
          // Refresh failed, log out
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            onboardingCompleted: false,
          });
          throw error;
        }
      },

      updateUser: (updates: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      setOnboardingCompleted: () => set({ onboardingCompleted: true }),

      clearError: () => set({ error: null }),

      clearPendingEmail: () => set({ pendingEmail: null, error: null }),
    }),
    {
      name: STORE_KEY,
      storage: authStorage,
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        onboardingCompleted: state.onboardingCompleted,
      }),
    }
  )
);
